import { healthAsParsed } from '../health/defaults';
import { ACTIVITY_CATEGORIES, HEALTH_PLATFORMS, WORKOUT_TIERS } from '../health/types';
import type { Err } from './common';
import type { Bundle } from './types';

/*
 * The health intake section (`healthRules`, health intake CONTRACT §7): a port of
 * packages/shared-cs/Runtime/Content/ContentValidator.Health.cs with the same messages in the same order. The server runs it last,
 * after the location themes. An absent section or block is its C# defaults; only an explicit `null` is missing. Change both together;
 * `validate.health.test.ts` pins every message. `warn` gets the one `ContentValidator.Warnings` finding of this section.
 */

const W = 'Health rules';
const positive = (v: number | null | undefined) =>
  typeof v === 'number' && Number.isFinite(v) && v > 0;
const fraction = (v: number | null | undefined) =>
  typeof v === 'number' && !Number.isNaN(v) && v >= 0 && v <= 1;
const n = (v: number | null | undefined) => v ?? 0;

export function validateHealth(b: Bundle, err: Err, warn?: Err) {
  const e = (path: string, message: string) => err('healthRules', undefined, path, message);
  if (b.healthRules === null) {
    e('', "Bundle is missing 'healthRules'.");
    return;
  }
  const h = healthAsParsed(b.healthRules)!;

  if (n(h.importDays) < 1) e('importDays', `${W} need importDays >= 1.`);
  if (n(h.syncIntervalMin) < 1) e('syncIntervalMin', `${W} need syncIntervalMin >= 1.`);
  if (n(h.maxWorkoutsPerDay) < 1) e('maxWorkoutsPerDay', `${W} need maxWorkoutsPerDay >= 1.`);
  if (!positive(h.maxEffortMinPerDay)) e('maxEffortMinPerDay', `${W} need maxEffortMinPerDay > 0.`);
  if (n(h.maxWorkoutSec) < 1) e('maxWorkoutSec', `${W} need maxWorkoutSec > 0.`);
  if (!positive(h.maxDayWorkoutMin)) e('maxDayWorkoutMin', `${W} need maxDayWorkoutMin > 0.`);
  if (n(h.lateDays) < 1) e('lateDays', `${W} need lateDays >= 1.`);
  if (n(h.futureSlackSec) < 0) e('futureSlackSec', `${W} need futureSlackSec >= 0.`);
  if ((h.enabled ?? true) && !(h.powerFamily ?? '').trim())
    e('powerFamily', `${W} need a powerFamily while enabled.`);

  const tc = h.tierCredit;
  if (tc != null && tc.length > 0) {
    if (tc.length !== WORKOUT_TIERS.count) {
      e('tierCredit', `${W} need tierCredit with 4 values (tiers 0..3).`);
    } else {
      const inRange = tc.every((v) => fraction(v));
      const ascending = tc.every((v, i) => i === 0 || v >= tc[i - 1]!);
      if (!inRange) e('tierCredit', `${W} need tierCredit values in 0..1.`);
      if (!ascending) e('tierCredit', `${W} need tierCredit ascending by tier.`);
    }
  }

  if (h.mets != null) {
    // Ordinal key order, like the C# (JS's default sort compares UTF-16 code units).
    for (const k of Object.keys(h.mets).sort()) {
      if (!(ACTIVITY_CATEGORIES as readonly string[]).includes(k))
        e(`mets.${k}`, `${W} mets has unknown category '${k}'.`);
      else if (!positive(h.mets[k])) e(`mets.${k}`, `${W} need mets.${k} > 0.`);
    }
  }

  const hr = h.hrBands;
  if (hr == null) {
    e('hrBands', `${W} are missing 'hrBands'.`);
  } else {
    if (!positive(hr.easyBelow) || !fraction(hr.hardAbove) || n(hr.easyBelow) > n(hr.hardAbove))
      e('hrBands.easyBelow', `${W} need 0 < hrBands.easyBelow <= hrBands.hardAbove <= 1.`);
    if (!positive(hr.easyMult) || !positive(hr.moderateMult) || !positive(hr.hardMult))
      e('hrBands.easyMult', `${W} need hrBands multipliers > 0.`);
    else if (n(hr.easyMult) > n(hr.moderateMult) || n(hr.moderateMult) > n(hr.hardMult))
      e('hrBands.easyMult', `${W} need hrBands.easyMult <= moderateMult <= hardMult.`);
  }

  if (!positive(h.metIntensityDivisor))
    e('metIntensityDivisor', `${W} need metIntensityDivisor > 0.`);
  const ic = h.intensityClamp;
  if (
    ic != null &&
    ic.length > 0 &&
    (ic.length !== 2 || !positive(ic[0]) || !positive(ic[1]) || ic[0]! > ic[1]!)
  )
    e('intensityClamp', `${W} need intensityClamp [min, max] with 0 < min <= max.`);

  const bd = h.bounds;
  if (bd == null) {
    e('bounds', `${W} are missing 'bounds'.`);
  } else {
    if (!positive(bd.runMaxKmh)) e('bounds.runMaxKmh', `${W} need bounds.runMaxKmh > 0.`);
    if (!positive(bd.walkMaxKmh)) e('bounds.walkMaxKmh', `${W} need bounds.walkMaxKmh > 0.`);
    if (!positive(bd.cycleMaxKmh)) e('bounds.cycleMaxKmh', `${W} need bounds.cycleMaxKmh > 0.`);
    if (positive(bd.walkMaxKmh) && positive(bd.runMaxKmh) && n(bd.walkMaxKmh) >= n(bd.runMaxKmh))
      e('bounds.walkMaxKmh', `${W} need bounds.walkMaxKmh < bounds.runMaxKmh.`);
    if (!positive(bd.hrOverMaxBpm)) e('bounds.hrOverMaxBpm', `${W} need bounds.hrOverMaxBpm > 0.`);
    if (!positive(bd.hrFlatBpm)) e('bounds.hrFlatBpm', `${W} need bounds.hrFlatBpm > 0.`);
    if (n(bd.hrFlatMinSamples) < 1)
      e('bounds.hrFlatMinSamples', `${W} need bounds.hrFlatMinSamples >= 1.`);
    if (!positive(bd.kcalPerMinMax))
      e('bounds.kcalPerMinMax', `${W} need bounds.kcalPerMinMax > 0.`);
    if (!positive(bd.defaultMaxHr)) e('bounds.defaultMaxHr', `${W} need bounds.defaultMaxHr > 0.`);
  }

  if (!fraction(h.overlapFraction)) e('overlapFraction', `${W} need overlapFraction in 0..1.`);
  if (!fraction(h.hrCoverageForTier3))
    e('hrCoverageForTier3', `${W} need hrCoverageForTier3 in 0..1.`);

  const bands = h.bands;
  if (bands == null) e('bands', `${W} are missing 'bands'.`);
  else if (
    !positive(bands.light) ||
    n(bands.light) >= n(bands.moderate) ||
    n(bands.moderate) >= n(bands.vigorous)
  )
    e('bands.light', `${W} need 0 < bands.light < bands.moderate < bands.vigorous.`);

  const lv = h.levels;
  if (lv == null) e('levels', `${W} are missing 'levels'.`);
  else if (
    !positive(lv.walker) ||
    n(lv.walker) >= n(lv.mover) ||
    n(lv.mover) >= n(lv.athlete) ||
    n(lv.athlete) >= n(lv.beast)
  )
    e(
      'levels.walker',
      `${W} need 0 < levels.walker < levels.mover < levels.athlete < levels.beast.`,
    );

  if (h.trustedSources != null) {
    const seen = new Set<string>();
    h.trustedSources.forEach((s, i) => {
      if (s == null || s.id == null || s.id.trim() === '') {
        e(`trustedSources.${i}.id`, `${W} trusted source #${i + 1} needs an id.`);
        return;
      }
      const ws = `${W} trusted source '${s.id}'`;
      const platform = s.platform ?? '';
      if (!(HEALTH_PLATFORMS as readonly string[]).includes(platform))
        e(
          `trustedSources.${i}.platform`,
          `${ws} has unknown platform '${platform}' (ios or android).`,
        );
      const tier = n(s.tier);
      if (tier < WORKOUT_TIERS.min || tier > WORKOUT_TIERS.max)
        e(`trustedSources.${i}.tier`, `${ws} needs a tier in 0..3.`);
      const key = `${platform}|${s.id}`;
      if (seen.has(key)) e(`trustedSources.${i}`, `${ws} is listed twice for ${platform}.`);
      seen.add(key);
    });
  }

  // `ContentValidator.Warnings` (HealthWarnings): a powerFamily no effect def has never lights the power-up look.
  const family = h.powerFamily ?? '';
  if (warn && family.trim() !== '' && !(b.statusEffects ?? []).some((x) => x?.family === family))
    warn(
      'healthRules',
      undefined,
      'powerFamily',
      `${W} powerFamily '${family}' matches no effect family.`,
    );
}
